// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import type LocalDate from 'lib-common/local-date'
import { useQueryResult } from 'lib-common/query'
import {
  defaultButtonTextStyle,
  useThrottledEventHandler
} from 'lib-components/atoms/buttons/button-commons'
import { zoomedMobileMax } from 'lib-components/breakpoints'
import ModalBackground from 'lib-components/molecules/modals/ModalBackground'
import { defaultMargins } from 'lib-components/white-space'
import { featureFlags } from 'lib-customizations/citizen'
import { faComment, faLockAlt } from 'lib-icons'
import { faCalendarPlus, faTreePalm, faUserMinus } from 'lib-icons'

import { useUser } from '../auth/state'
import { useTranslation } from '../localization'
import { mobileBottomNavHeight } from '../navigation/const'

import { activeQuestionnaireQuery } from './queries'
import { isQuestionnaireAvailable } from './utils'

interface Props {
  close: () => void
  openReservations: () => void
  openAbsences: (initialDate: LocalDate | undefined) => void
  openHolidays: () => void
  openDiscussionReservations: (initialDate: LocalDate | undefined) => void
  isDiscussionActionVisible: boolean
}

export default React.memo(function ActionPickerModal({
  close,
  openReservations,
  openAbsences,
  openHolidays,
  openDiscussionReservations,
  isDiscussionActionVisible
}: Props) {
  const i18n = useTranslation()
  const onCreateAbsences = useCallback(
    () => openAbsences(undefined),
    [openAbsences]
  )
  const onOpenDiscussionReservation = useCallback(
    () => openDiscussionReservations(undefined),
    [openDiscussionReservations]
  )
  const questionnaireAvailable = isQuestionnaireAvailable(
    useQueryResult(activeQuestionnaireQuery()),
    useUser()
  )

  return (
    <ModalBackground onClick={close} onEscapeKey={close}>
      <Container
        role="dialog"
        aria-modal="true"
        aria-label={
          featureFlags.discussionReservations && isDiscussionActionVisible
            ? i18n.calendar.newReservationOrAbsenceOrDiscussion
            : i18n.calendar.newReservationOrAbsence
        }
      >
        {questionnaireAvailable && (
          <Action onClick={openHolidays} data-qa="calendar-action-holidays">
            {questionnaireAvailable === 'with-strong-auth' ? (
              <LabelContainer>
                <FontAwesomeIcon icon={faLockAlt} />
                {i18n.calendar.newHoliday}
              </LabelContainer>
            ) : (
              i18n.calendar.newHoliday
            )}
            <IconBackground>
              <FontAwesomeIcon icon={faTreePalm} size="1x" />
            </IconBackground>
          </Action>
        )}
        {featureFlags.discussionReservations && isDiscussionActionVisible && (
          <Action
            onClick={onOpenDiscussionReservation}
            data-qa="calendar-action-discussions"
          >
            {i18n.calendar.discussionTimeReservation.surveyModalButtonText}
            <IconBackground>
              <FontAwesomeIcon icon={faComment} size="1x" />
            </IconBackground>
          </Action>
        )}
        <Action onClick={onCreateAbsences} data-qa="calendar-action-absences">
          {i18n.calendar.newAbsence}
          <IconBackground>
            <FontAwesomeIcon icon={faUserMinus} size="1x" />
          </IconBackground>
        </Action>
        <Action
          onClick={openReservations}
          data-qa="calendar-action-reservations"
        >
          {i18n.calendar.newReservationBtn}
          <IconBackground>
            <FontAwesomeIcon icon={faCalendarPlus} size="1x" />
          </IconBackground>
        </Action>
      </Container>
    </ModalBackground>
  )
})

const baseBottomValue = 46

const Container = styled.div`
  position: fixed;
  bottom: calc(
    ${baseBottomValue}px + ${defaultMargins.L} + ${mobileBottomNavHeight}px
  );
  right: calc(${defaultMargins.s} + env(safe-area-inset-right));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  gap: ${defaultMargins.s};
  @media (max-width: ${zoomedMobileMax}) {
    align-items: flex-start;
    left: 0;
    right: 0;
    padding: 0 ${defaultMargins.xs};
    row-gap: clamp(4px, 1.5vh, ${defaultMargins.xs});
    bottom: calc(
      ${baseBottomValue}px + ${defaultMargins.s} + ${mobileBottomNavHeight}px
    );
    button {
      white-space: normal;
      text-align: left;
      min-height: ${defaultMargins.L};
    }
  }
`

function Action({
  onClick,
  children,
  'data-qa': dataQa
}: {
  onClick: () => void
  children: React.ReactNode
  'data-qa': string
}) {
  const handleOnClick = useThrottledEventHandler(onClick)
  return (
    <StyledAction onClick={handleOnClick} data-qa={dataQa}>
      {children}
    </StyledAction>
  )
}

const StyledAction = styled.button.attrs({ type: 'button' })`
  ${defaultButtonTextStyle};
  color: ${(p) => p.theme.colors.grayscale.g0};
  border: none;
  background: none;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.main.m2Focus};
    outline-offset: 2px;
  }

  &:hover {
    color: ${(p) => p.theme.colors.main.m2Hover};
  }

  &:active {
    color: ${(p) => p.theme.colors.main.m2Active};
  }

  svg {
    font-size: 1.25em;
  }
`

const LabelContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${defaultMargins.xs};
`

const IconBackground = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  color: ${(p) => p.theme.colors.main.m2};
  background: ${(p) => p.theme.colors.grayscale.g0};
  margin-left: ${defaultMargins.s};
  border-radius: 50%;
  @media (max-width: ${zoomedMobileMax}) {
    display: none;
  }
`
